import { ArrowRight } from "lucide-react";

/**
 * Anonymized engagement story.
 * Replace the SITUATION / FINDINGS / FIX / OUTCOME copy with real
 * (or further anonymized) numbers from a recent engagement.
 */
export default function ArqEngagementStory() {
  return (
    <section className="bg-arq-bg py-20 md:py-[100px] px-6 border-t border-arq-border/60">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center max-w-[820px] mx-auto mb-12">
          <div className="arq-label mb-6">Representative Scenario</div>
          <h2
            className="font-display font-semibold text-arq-text mb-5"
            style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
          >
            Enterprise Review Supported by Validated AI Risk Evidence.
          </h2>
          <p className="text-arq-muted text-[16px] leading-[1.75]">
            Composite scenario reflecting a common readiness engagement pattern. Only verified,
            client-approved results appear in published case studies.
          </p>
        </div>

        <div className="arq-card p-8 md:p-10">
          <div className="grid md:grid-cols-4 gap-8 md:gap-6">
            {/* Situation */}
            <div>
              <div className="font-mono-tech text-[11px] text-arq-blue tracking-[0.08em] mb-3">
                01 · SITUATION
              </div>
              <p className="text-arq-text text-[15px] leading-[1.7]">
                An AI-enabled fintech was preparing to expand an agentic underwriting workflow
                while responding to enterprise customer questions about AI risk, access control,
                audit logging, and compliance readiness.
              </p>
            </div>

            {/* Surfaced */}
            <div>
              <div className="font-mono-tech text-[11px] text-arq-blue tracking-[0.08em] mb-3">
                02 · WHAT SURFACED
              </div>
              <ul className="space-y-2 text-arq-text text-[14px] leading-[1.6]">
                <li className="flex gap-2"><span className="text-arq-danger font-mono">●</span><span>Agent authorization bypass</span></li>
                <li className="flex gap-2"><span className="text-arq-danger font-mono">●</span><span>Prompt injection path to tool use</span></li>
                <li className="flex gap-2"><span className="font-mono" style={{ color: "#D97706" }}>●</span><span>API authorization gaps</span></li>
                <li className="flex gap-2"><span className="font-mono" style={{ color: "#D97706" }}>●</span><span>Missing approval gates for high-impact actions</span></li>
                <li className="flex gap-2"><span className="text-arq-teal font-mono">●</span><span>Incomplete audit logging</span></li>
                <li className="flex gap-2"><span className="text-arq-teal font-mono">●</span><span>Unclear evidence for external review</span></li>
              </ul>
            </div>

            {/* Changed */}
            <div>
              <div className="font-mono-tech text-[11px] text-arq-blue tracking-[0.08em] mb-3">
                03 · WHAT CHANGED
              </div>
              <p className="text-arq-text text-[15px] leading-[1.7]">
                ArqeraAI helped define remediation priorities, tighten agent authorization
                boundaries, add approval controls, validate fixes, and assemble evidence
                suitable for external and leadership review.
              </p>
            </div>

            {/* Outcome */}
            <div>
              <div className="font-mono-tech text-[11px] text-arq-teal tracking-[0.08em] mb-3">
                04 · OUTCOME
              </div>
              <p className="text-arq-text text-[15px] leading-[1.7]">
                The team moved from unclear AI exposure to documented, validated control
                evidence that supported external review and leadership decision-making.
              </p>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-arq-border flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-arq-muted text-[14px] italic">
              Every engagement is designed to produce clear findings, practical remediation,
              validated controls, and evidence that can withstand scrutiny.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 text-arq-blue text-[14px] font-semibold hover:text-arq-blue-bright transition-colors"
            >
              Discuss your environment <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
